import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy } from 'lucide-react';

// Sound generator functions using Web Audio API
const createAudioContext = () => {
  return new (window.AudioContext || window.webkitAudioContext)();
};

const playSpinSound = (audioContext, isSlowingDown = false) => {
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  // Click/tick sound
  oscillator.type = 'square';
  oscillator.frequency.setValueAtTime(isSlowingDown ? 800 : 1200, audioContext.currentTime);
  
  gainNode.gain.setValueAtTime(0.1, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.05);
  
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + 0.05);
};

const playWinnerFanfare = (audioContext) => {
  const notes = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
  const duration = 0.15;
  
  notes.forEach((freq, index) => {
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(freq, audioContext.currentTime);
    
    const startTime = audioContext.currentTime + (index * duration);
    gainNode.gain.setValueAtTime(0, startTime);
    gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.02);
    gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration + 0.1);
    
    oscillator.start(startTime);
    oscillator.stop(startTime + duration + 0.15);
  });
  
  // Final triumphant chord
  setTimeout(() => {
    const chordFreqs = [523.25, 659.25, 783.99];
    chordFreqs.forEach(freq => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(freq, audioContext.currentTime);
      
      gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.8);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.8);
    });
  }, notes.length * duration * 1000);
};

const SlotMachineRoulette = ({ tickets, winningTicket, isSpinning, onSpinComplete }) => {
  const [displayTickets, setDisplayTickets] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(3); // Start at 3 so we can show tickets above and below
  const [showWinner, setShowWinner] = useState(false);
  const spinRef = useRef(null);
  const audioContextRef = useRef(null);
  const winnerIndexRef = useRef(0);
  const lastIndexRef = useRef(3); // Track the last index for sound sync

  // Initialize audio context on first interaction
  useEffect(() => {
    if (isSpinning && !audioContextRef.current) {
      audioContextRef.current = createAudioContext();
    }
  }, [isSpinning]);

  // Create a shuffled array of tickets for the spinning effect
  // IMPORTANT: Ensure the winning ticket is at the exact final position
  useEffect(() => {
    if (tickets && tickets.length > 0) {
      // Use winning ticket if available, otherwise pick a random one (will be updated later)
      const targetTicket = winningTicket || tickets[0];
      
      // Create many copies for smooth 15-second spinning
      const shuffled = [];
      for (let i = 0; i < 20; i++) {  // More copies for longer spin
        // Shuffle but exclude the winning ticket to avoid duplicates near the end
        const filtered = tickets.filter(t => t !== targetTicket);
        if (filtered.length > 0) {
          shuffled.push(...[...filtered].sort(() => Math.random() - 0.5));
        } else {
          // If only one ticket type, just add all tickets
          shuffled.push(...[...tickets].sort(() => Math.random() - 0.5));
        }
      }
      
      // Add some random tickets before the winner
      for (let i = 0; i < 5; i++) {
        const randomTicket = tickets[Math.floor(Math.random() * tickets.length)];
        if (randomTicket !== targetTicket) {
          shuffled.push(randomTicket);
        } else {
          const alt = tickets.find(t => t !== targetTicket);
          shuffled.push(alt || tickets[0]);
        }
      }
      
      // Place the winning ticket at the exact position that will be centered when spin stops
      shuffled.push(targetTicket);
      
      // Add a few more after for visual effect
      for (let i = 0; i < 3; i++) {
        const randomTicket = tickets[Math.floor(Math.random() * tickets.length)];
        shuffled.push(randomTicket !== targetTicket ? randomTicket : tickets[0]);
      }
      
      winnerIndexRef.current = shuffled.length - 4; // The winning ticket index
      setDisplayTickets(shuffled);
    }
  }, [tickets, winningTicket]);

  // Handle the spinning animation - 15 seconds total
  useEffect(() => {
    if (isSpinning && displayTickets.length > 0) {
      const TOTAL_SPIN_TIME = 15000; // 15 seconds in milliseconds
      const startTime = Date.now();
      const startIndex = 3; // We start at index 3
      const finalIndex = winnerIndexRef.current;
      const totalDistance = finalIndex - startIndex;
      lastIndexRef.current = startIndex; // Reset last index
      
      const spin = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / TOTAL_SPIN_TIME, 1);
        
        // Calculate current index based on eased progress
        // Use easeOutQuart for smooth deceleration
        const easedProgress = 1 - Math.pow(1 - progress, 4);
        const targetIndex = startIndex + Math.floor(easedProgress * totalDistance);
        
        // Only update and play sound if we've moved to a new ticket
        if (targetIndex !== lastIndexRef.current) {
          // Play sound for EACH ticket that we pass
          const ticketsPassed = targetIndex - lastIndexRef.current;
          if (audioContextRef.current) {
            // Play a tick for each ticket passed (but limit to avoid audio overload)
            const ticksToPlay = Math.min(ticketsPassed, 3);
            for (let i = 0; i < ticksToPlay; i++) {
              setTimeout(() => {
                if (audioContextRef.current) {
                  const isSlowing = progress > 0.6;
                  playSpinSound(audioContextRef.current, isSlowing);
                }
              }, i * 20); // Slight delay between rapid ticks
            }
          }
          
          lastIndexRef.current = targetIndex;
          setCurrentIndex(targetIndex);
        }
        
        if (progress < 1) {
          // Use requestAnimationFrame for smoother animation
          spinRef.current = requestAnimationFrame(spin);
        } else {
          // Ensure we're exactly on the final index
          if (lastIndexRef.current !== finalIndex) {
            // Play final tick
            if (audioContextRef.current) {
              playSpinSound(audioContextRef.current, true);
            }
          }
          setCurrentIndex(finalIndex);
          lastIndexRef.current = finalIndex;
          
          // Final position - show winner after a brief pause
          setTimeout(() => {
            setShowWinner(true);
            // Play winner fanfare
            if (audioContextRef.current) {
              playWinnerFanfare(audioContextRef.current);
            }
            if (onSpinComplete) {
              onSpinComplete();
            }
          }, 600);
        }
      };
      
      spinRef.current = requestAnimationFrame(spin);
      
      return () => {
        if (spinRef.current) {
          cancelAnimationFrame(spinRef.current);
        }
      };
    }
  }, [isSpinning, displayTickets, onSpinComplete]);

  // Get visible tickets (3 above, current, 3 below)
  const getVisibleTickets = () => {
    const visible = [];
    for (let i = -3; i <= 3; i++) {
      const idx = currentIndex + i;
      if (idx >= 0 && idx < displayTickets.length) {
        visible.push({
          ticket: displayTickets[idx],
          position: i,
          isCenter: i === 0
        });
      }
    }
    return visible;
  };

  return (
    <div className="relative flex flex-col items-center">
      {/* Slot Machine Frame - hidden when winner is shown */}
      {!showWinner && (
        <div className="relative bg-gradient-to-b from-[#27272a] to-[#18181b] rounded-3xl p-8 border-4 border-[#EAB308] shadow-2xl">
          {/* Slot window */}
          <div className="relative w-48 h-80 bg-[#09090b] rounded-2xl overflow-hidden border-4 border-[#52525b]">
            {/* Gradient overlays for fade effect */}
            <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-[#09090b] to-transparent z-10 pointer-events-none" />
            <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#09090b] to-transparent z-10 pointer-events-none" />
            
            {/* Center highlight line */}
            <div className="absolute top-1/2 left-0 right-0 -translate-y-1/2 h-16 border-y-2 border-[#EAB308] bg-[#EAB308]/10 z-5" />
          
          {/* Tickets display */}
          <div className="relative h-full flex flex-col items-center justify-center z-20">
            {getVisibleTickets().map(({ ticket, position, isCenter }) => (
              <motion.div
                key={`${ticket}-${position}`}
                animate={{ 
                  y: position * 45,
                  opacity: Math.abs(position) <= 1 ? 1 : 0.4,
                  scale: isCenter ? 1.2 : 0.85
                }}
                transition={{ duration: 0.08 }}
                className="absolute flex items-center justify-center"
                style={{ zIndex: isCenter ? 30 : 10 }}
              >
                <div className={`
                  px-6 py-3 rounded-xl font-mono text-2xl font-bold
                  ${isCenter 
                    ? 'bg-[#EAB308] text-black shadow-lg shadow-[#EAB308]/50' 
                    : 'bg-[#27272a] text-[#a1a1aa]'
                  }
                `}>
                  #{ticket}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* Side lights */}
        <div className="absolute left-2 top-1/2 -translate-y-1/2 flex flex-col gap-2">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              animate={{ 
                backgroundColor: isSpinning 
                  ? ['#EAB308', '#f43f5e', '#A855F7', '#10b981', '#EAB308']
                  : '#52525b'
              }}
              transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.1 }}
              className="w-3 h-3 rounded-full"
            />
          ))}
        </div>
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex flex-col gap-2">
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              animate={{ 
                backgroundColor: isSpinning 
                  ? ['#A855F7', '#EAB308', '#10b981', '#f43f5e', '#A855F7']
                  : '#52525b'
              }}
              transition={{ duration: 0.5, repeat: Infinity, delay: i * 0.1 }}
              className="w-3 h-3 rounded-full"
            />
          ))}
        </div>
      </div>
      )}

      {/* Winner Reveal - replaces the slot machine */}
      <AnimatePresence>
        {showWinner && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center justify-center"
          >
            <div className="bg-gradient-to-b from-[#27272a] to-[#18181b] rounded-3xl p-10 border-4 border-[#EAB308] text-center shadow-2xl">
              <motion.div
                animate={{ rotate: [0, -10, 10, -10, 10, 0] }}
                transition={{ duration: 0.5, repeat: 2 }}
              >
                <Trophy className="w-20 h-20 text-[#EAB308] mx-auto mb-4" />
              </motion.div>
              <h3 className="text-3xl font-bold text-white mb-4">WINNER!</h3>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: 'spring', bounce: 0.5 }}
                className="bg-[#EAB308] text-black font-mono text-4xl font-bold px-10 py-5 rounded-xl shadow-lg shadow-[#EAB308]/50"
              >
                #{winningTicket}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SlotMachineRoulette;
